// frontend/packages/business-console/src/pages/WorkflowForms.jsx
//
// Work item 4.11 (partial) — top-level Workflow Forms list page.
//
// Lists currently-published workflow form definitions (latest version
// per slug). The full 4.11 brief also includes a "Forms" tab on the
// Customer profile + CSV export — those land in follow-up PRs once
// the basic fill loop is exercised.
//
// `/business/:slug/forms`

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { BusinessContext } from '../contexts/BusinessContext';
import { useAuth } from '../contexts/AuthContext';
import {
  listWorkflowForms,
  deleteWorkflowForm,
} from '../services/workflowFormService';
import { useTokens, Surface, PageHeader } from '../components/primitives';

function formatDate(input) {
  if (!input) return '—';
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function WorkflowForms() {
  const { business } = useContext(BusinessContext);
  const { user, businessId, isStaff } = useAuth();
  const t = useTokens();
  const navigate = useNavigate();
  const basePath = business?.slug ? `/business/${business.slug}` : '';

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  // Delete is an admin action (techs/managers fill; admins curate). Mirror
  // the backend gate (canAuthorForms = businessadmin for the active
  // business OR UpRyzze staff) so non-admins don't see a button that 403s.
  const canDelete = useMemo(() => {
    if (isStaff) return true;
    const access = Array.isArray(user?.businessAccess)
      ? user.businessAccess
      : [];
    return access.some(
      (e) =>
        String(e?.businessId) === String(businessId) &&
        String(e?.role || '').toLowerCase() === 'businessadmin'
    );
  }, [user, businessId, isStaff]);

  // Confirm-before-delete dialog state + in-flight guard.
  const [deleteTarget, setDeleteTarget] = useState(null); // the form row
  const [deleting, setDeleting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listWorkflowForms();
      setItems(res.items || []);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Couldn't load workflow forms."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteWorkflowForm(deleteTarget._id);
      setItems((prev) => prev.filter((f) => f.slug !== deleteTarget.slug));
      setNotice(`Deleted “${deleteTarget.name}”.`);
      setDeleteTarget(null);
    } catch (err) {
      const status = err?.response?.status;
      setError(
        status === 403
          ? 'Only business admins can delete forms.'
          : err?.response?.data?.message ||
              err?.message ||
              "Couldn't delete the form."
      );
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <PageHeader
        title="Workflow forms"
        sx={{ mb: 2 }}
        meta={
          <Typography sx={{ fontSize: 13, color: t.muted, fontWeight: 600 }}>
            Published intake, assessment, and checklist forms for the field.
          </Typography>
        }
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={refresh} disabled={loading}>
                <RefreshRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              size="small"
              component={RouterLink}
              to={`${basePath}/forms/drafts`}
              sx={{ textTransform: 'none' }}
            >
              Offline drafts
            </Button>
            <Button
              size="small"
              variant="contained"
              component={RouterLink}
              to={`${basePath}/forms/ai-builder`}
              sx={{ textTransform: 'none' }}
            >
              Build with AI
            </Button>
          </Stack>
        }
      />

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      {notice ? (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setNotice(null)}
        >
          {notice}
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Surface sx={{ p: 4, textAlign: 'center' }}>
          <DescriptionOutlinedIcon
            sx={{ fontSize: 40, color: t.faint, mb: 1 }}
          />
          <Typography sx={{ color: t.muted }}>
            No workflow forms published yet. Once a form is authored and
            published in the Tools console, it&apos;ll show up here for your
            team to fill in the field.
          </Typography>
        </Surface>
      ) : (
        <TableContainer component={Surface} sx={{ overflow: 'hidden' }}>
          <Table
            size="small"
            sx={{
              '& th': {
                fontWeight: 800,
                fontSize: 10,
                letterSpacing: '.4px',
                textTransform: 'uppercase',
                color: t.faint,
                bgcolor: t.tableHead,
                borderBottom: `1px solid ${t.hairline}`,
              },
              '& td': {
                borderBottom: `1px solid ${t.hairline}`,
                color: t.text,
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Slug</TableCell>
                <TableCell align="right">Version</TableCell>
                <TableCell>Published</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((f) => (
                <TableRow
                  key={f._id}
                  hover
                  sx={{ cursor: 'pointer', '&:hover': { bgcolor: t.subtle } }}
                  onClick={() => navigate(`${basePath}/forms/${f.slug}/new`)}
                >
                  <TableCell sx={{ fontWeight: 700 }}>{f.name}</TableCell>
                  <TableCell sx={{ color: t.muted }}>
                    <code>{f.slug}</code>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    v{f.version}
                  </TableCell>
                  <TableCell sx={{ color: t.faint }}>
                    {formatDate(f.publishedAt)}
                  </TableCell>
                  <TableCell align="right">
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="flex-end"
                      alignItems="center"
                    >
                      <Button
                        size="small"
                        variant="contained"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`${basePath}/forms/${f.slug}/new`);
                        }}
                      >
                        Fill
                      </Button>
                      {canDelete ? (
                        <Tooltip title="Delete form">
                          <IconButton
                            size="small"
                            aria-label={`Delete ${f.name}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTarget(f);
                            }}
                            sx={{ color: 'text.secondary' }}
                          >
                            <DeleteOutlineRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => (deleting ? null : setDeleteTarget(null))}
      >
        <DialogTitle>Delete this form?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            “{deleteTarget?.name}” will be removed from your team&apos;s forms
            list. Submissions your team has already filled out are kept. You can
            ask UpRyzze support to restore a deleted form if you change your
            mind.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" onClick={confirmDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
